import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useUser } from '../context/UserContext';
import { useWallet } from '../context/WalletContext';
import Navbar from './Navbar';
import { 
  Clock, 
  Users, 
  Wallet,
  Target,
  Plus,
  Star,
  Timer,
  Coins,
  Award,
  Gift,
  ArrowRight,
  ArrowLeft,
  Play,
  CheckCircle,
  Shield
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useUser();
  const { wallet, initiatePayment } = useWallet();
  const [liveContests, setLiveContests] = useState([]);
  const [upcomingContests, setUpcomingContests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch live and upcoming contests
  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    // Demo data for immediate display
    const demoLiveContests = [
      {
        id: "live_1",
        title: "Sports Quiz Championship",
        description: "Test your sports knowledge and win big!",
        first_prize: "Residential Land Worth ₹5,00,000",
        prize_pool: "₹8,00,000",
        total_participants: 500,
        enrolled_count: 324,
        status: "live"
      },
      {
        id: "live_2", 
        title: "Entertainment Quiz Challenge",
        description: "Movies, music, and celebrity trivia!",
        first_prize: "Agricultural Land Worth ₹3,00,000",
        prize_pool: "₹6,50,000",
        total_participants: 1000,
        enrolled_count: 567,
        status: "live"
      },
      {
        id: "live_3",
        title: "General Knowledge Quiz", 
        description: "Ultimate knowledge challenge for everyone!",
        first_prize: "Commercial Land Worth ₹4,00,000",
        prize_pool: "₹7,50,000",
        total_participants: 750,
        enrolled_count: 445,
        status: "live"
      }
    ];

    const demoUpcomingContests = [
      {
        id: "upcoming_1",
        title: "Evening Knowledge Battle",
        description: "Perfect way to end your day with some brain exercise",
        prize_pool: 25000,
        entry_fee: 100,
        start_time: "6:00 PM Today",
        participants: "250/500"
      },
      {
        id: "upcoming_2", 
        title: "Weekend Special Quiz",
        description: "Special weekend contest with higher rewards",
        prize_pool: 100000,
        entry_fee: 200,
        start_time: "Tomorrow 2:00 PM",
        participants: "145/1000"
      },
      {
        id: "upcoming_3",
        title: "Monday Motivation Quiz",
        description: "Start your week with some challenging questions", 
        prize_pool: 75000,
        entry_fee: 150,
        start_time: "Monday 9:00 AM",
        participants: "89/750"
      }
    ];

    // Set demo data immediately
    setLiveContests(demoLiveContests);
    setUpcomingContests(demoUpcomingContests);
    setLoading(false);

    // Try to fetch from backend in background (for future updates)
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';
      
      // Fetch live contests
      const liveResponse = await fetch(`/api/contests/live`);
      if (liveResponse.ok) {
        const liveData = await liveResponse.json();
        if (Array.isArray(liveData) && liveData.length > 0) {
          setLiveContests(liveData);
        }
      }
      
      // Fetch upcoming contests  
      const upcomingResponse = await fetch(`/api/contests/upcoming`);
      if (upcomingResponse.ok) {
        const upcomingData = await upcomingResponse.json();
        if (Array.isArray(upcomingData) && upcomingData.length > 0) {
          setUpcomingContests(upcomingData);
        }
      }
    } catch (error) {
      console.log('Backend not available, using demo data:', error);
      // Demo data already set, no need to handle error
    }
  };

  const handleParticipateInContest = (contestId) => {
    // Navigate to contest details page
    window.location.href = `/contest/${contestId}`;
  };

  const handleAddMoney = async () => {
    await initiatePayment(
      500, // Default amount
      (data) => {
        alert('Money added successfully!');
      },
      (error) => {
        alert(`Failed to add money: ${error}`);
      }
    );
  };

  const handleTryDemo = () => {
    alert('Demo contest feature available on landing page!');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Contest Cards - Vertical Layout */}
            {liveContests && liveContests.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">🔥 Live Contests</h2>
                {liveContests.map((contest, index) => (
                  <Card key={contest.id} className="border border-gray-200 bg-white shadow-lg relative overflow-hidden">
                    <CardHeader className="pb-4 px-6 pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge className="bg-red-600 text-white text-xs">🔥 LIVE</Badge>
                            {contest.enrolled_count > 200 && (
                              <Badge className="bg-green-600 text-white text-xs">🎯 HIGH PARTICIPATION</Badge>
                            )}
                          </div>
                          <CardTitle className="text-xl text-gray-900 mb-1">{contest.title}</CardTitle>
                          <p className="text-gray-500 text-sm">{contest.description}</p>
                        </div>
                        <div className="text-right pl-4">
                          <div className="text-2xl font-bold text-red-600">{contest.prize_pool}</div>
                          <div className="text-xs text-gray-500">Prize Pool</div>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0 px-6 pb-6">
                      {/* Prize Display with Icon */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm text-gray-600 mb-1">🏆 1st Prize</div>
                            <div className="text-lg font-bold text-gray-900">{contest.first_prize}</div>
                          </div>
                          <div className="text-right">
                            <div className={`flex items-center space-x-2 text-sm ${
                              contest.enrolled_count > contest.total_participants * 0.6 ? 'text-green-600 font-semibold' : 'text-gray-600'
                            }`}>
                              <Users className={`h-4 w-4 ${contest.enrolled_count > contest.total_participants * 0.6 ? 'text-green-600' : ''}`} />
                              <span>{contest.enrolled_count}/{contest.total_participants}</span>
                            </div>
                            <div className={`text-xs ${
                              contest.enrolled_count > contest.total_participants * 0.6 ? 'text-green-500' : 'text-gray-500'
                            }`}>
                              {contest.enrolled_count > contest.total_participants * 0.6 ? '✨ High Interest!' : 'Participated'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Join Button */}
                      <Button 
                        onClick={() => handleParticipateInContest(contest.id)}
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-3 text-base font-medium rounded-lg"
                      >
                        🎯 Join Contest
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {loading && (
              <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading contests...</p>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Contests */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Upcoming Contests</CardTitle>
                <p className="text-gray-600">Get ready for these amazing contests</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {upcomingContests && upcomingContests.length > 0 ? upcomingContests.map((contest, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 border-gray-300">
                    <div className="flex items-center space-x-4">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{contest.title}</h3>
                        <p className="text-sm text-gray-600">
                          {typeof contest.entry_fee === 'number' ? `₹${contest.entry_fee}` : contest.entry_fee} entry | 
                          {typeof contest.prize_pool === 'number' ? ` ₹${contest.prize_pool.toLocaleString()}` : ` ${contest.prize_pool}`} pool
                        </p>
                        <p className="text-xs text-gray-500">{contest.participants} participants</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 font-medium">{contest.start_time}</div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-1 cursor-not-allowed opacity-50 bg-gray-100 text-gray-500 border-gray-300"
                        disabled={true}
                      >
                        🔜 Coming Soon
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-6 text-gray-500">
                    Loading upcoming contests...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How To Participate */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <CheckCircle className="h-6 w-6 mr-2 text-green-600" />
                  How To Participate
                </CardTitle>
                <p className="text-gray-600">Master the contest in 3 simple steps</p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { step: 1, title: 'Add Funds', desc: 'Add money to your wallet securely for contest participation', icon: Coins, color: 'blue' },
                    { step: 2, title: 'Join Contest', desc: 'Choose from Sports, Entertainment, or Knowledge contests', icon: Target, color: 'green' },
                    { step: 3, title: 'Win Prizes', desc: 'Answer correctly and win land, gadgets & cash prizes', icon: Award, color: 'purple' }
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className={`bg-${item.color}-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-${item.color}-200`}>
                        <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-2">Step {item.step}: {item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <Button 
                  onClick={handleTryDemo}
                  variant="outline" 
                  className="w-full mt-8 py-3 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-medium rounded-lg transition-all duration-200"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Try Demo Contest
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">


            {/* Wallet Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Wallet Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Wallet className="h-5 w-5 text-red-600" />
                    <span className="text-gray-700">Total Amount</span>
                  </div>
                  <span className="font-bold text-gray-900">₹{wallet?.total_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Gift className="h-5 w-5 text-purple-600" />
                    <span className="text-gray-700">Bonus Cash</span>
                  </div>
                  <span className="font-bold text-gray-900">₹{wallet?.bonus_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="space-y-2">
                  <Button onClick={handleAddMoney} className="w-full bg-red-600 hover:bg-red-700">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Money
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Verification Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-700">Phone</span>
                  </div>
                  {user.phone_verified ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <Shield className="h-5 w-5 text-orange-600" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-700">Email</span>
                  </div>
                  {user.email_verified ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <Shield className="h-5 w-5 text-orange-600" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-700">KYC</span>
                  </div>
                  {user.kyc_verified ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <Shield className="h-5 w-5 text-orange-600" />
                  )}
                </div>
                
                {!user.kyc_verified && (
                  <Button variant="outline" className="w-full mt-3" size="sm">
                    Complete Verification
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-8 border-t border-gray-200">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <a href="/help" className="hover:text-red-600 transition-colors">Help</a>
            <a href="#" className="hover:text-red-600 transition-colors">Fair Participation Policy</a>
            <a href="#" className="hover:text-red-600 transition-colors">Contact Support</a>
          </div>
          <p className="text-center text-xs text-gray-500 mt-4">
            Skill-based quiz platform. No gambling. 18+ only.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;